import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import { FiArrowRight, FiCheckCircle, FiClock, FiCopy, FiCreditCard, FiFileText, FiLock, FiMail, FiRefreshCw, FiShield, FiTrash2, FiTrendingDown, FiTv, FiUploadCloud } from "react-icons/fi";
import { useAuth } from "../context/AuthContext.tsx";
import { connectors } from "../data/connectors";
import { ConnectorLogo } from "../components/ConnectorLogo";
import "./ReceiptEvidencePage.css";

type Evidence = {
  id:string;kind:"receipt"|"subscription";method:"email"|"upload";
  status:"uploading"|"pending"|"verified"|"rejected";merchant:string|null;serviceKey:string|null;
  observedAt:string|null;verifiedAt:string|null;createdAt:string;hasPrivateFile:boolean;
};
type SubscriptionSignal = {
  serviceKey:string;confidence:number;status:"current"|"stale"|"expired";lastEvidenceAt:string;
  distinctRenewals:number;verifiedEvidenceCount:number;daysSinceVerification:number;
  refreshRecommendedAt:string;expiresAt:string;note:string;
};
type EvidenceResponse={
  records:Evidence[];subscriptions:SubscriptionSignal[];forwardingAddress:string|null;
  uploadsEnabled:boolean;emailEnabled:boolean;
};
const API=process.env.NODE_ENV==="production"?"/api":(process.env.REACT_APP_BACKEND_URL||"/api");
const streaming=connectors.filter(c=>c.category==="Streaming");
const labels:Record<string,string>={pending:"Pending review",verified:"Verified",rejected:"Rejected",uploading:"Uploading"};
const today=new Date().toISOString().slice(0,10);
const numberDays=30;
const demoFreshness=(days:number)=>Math.round(55*Math.pow(0.5,days/numberDays));

const ReceiptEvidencePage:React.FC=()=>{
  const {user,isAuthenticated,isLoading,loginWithGoogle}=useAuth();
  const [searchParams,setSearchParams]=useSearchParams();
  const tab=searchParams.get("tab")==="subscriptions"?"subscriptions":"receipts";
  const initialService=searchParams.get("service")||"netflix";
  const [service,setService]=useState(streaming.some(s=>s.key===initialService)?initialService:"netflix");
  const [merchant,setMerchant]=useState("");
  const [purchaseDate,setPurchaseDate]=useState(today);
  const [file,setFile]=useState<File|null>(null);
  const [data,setData]=useState<EvidenceResponse|null>(null);
  const [loading,setLoading]=useState(false);
  const [submitting,setSubmitting]=useState(false);
  const [message,setMessage]=useState("");
  const [error,setError]=useState("");
  const [days,setDays]=useState(0);
  const [copied,setCopied]=useState(false);

  const load=useCallback(async()=>{
    if(!isAuthenticated){setData(null);return;}
    setLoading(true);setError("");
    try{
      const res=await fetch(API+"/evidence",{credentials:"include"});
      if(!res.ok)throw new Error(res.status===500?"Evidence tables may need the new Prisma migration.":"Unable to load your evidence");
      setData(await res.json());
    }catch(e){setError(e instanceof Error?e.message:"Could not load evidence");}
    finally{setLoading(false);}
  },[isAuthenticated]);

  useEffect(()=>{void load();},[load]);
  useEffect(()=>{
    const queryService=searchParams.get("service");
    if(queryService && streaming.some(s=>s.key===queryService))setService(queryService);
  },[searchParams]);
  const setTab=(next:"receipts"|"subscriptions")=>{
    setSearchParams(next==="receipts"?{tab:"receipts"}:{tab:"subscriptions",service});
    setMessage("");setError("");
  };
  const upload=async(event:React.FormEvent)=>{
    event.preventDefault();setError("");setMessage("");
    if(!file){setError("Choose an image first.");return;}
    const allowed=["image/jpeg","image/png","image/webp"];
    if(!allowed.includes(file.type) || file.size>6*1024*1024 || file.size===0){
      setError("Choose a JPEG, PNG or WebP under 6 MB.");return;
    }
    if(!isAuthenticated){loginWithGoogle();return;}
    const url=process.env.REACT_APP_SUPABASE_URL;
    const anonKey=process.env.REACT_APP_SUPABASE_ANON_KEY;
    if(!url||!anonKey){setError("Signed upload is not configured in the preview environment.");return;}
    setSubmitting(true);
    try{
      const intent=await fetch(API+"/evidence/upload",{
        method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({kind:tab==="receipts"?"receipt":"subscription",merchant:tab==="receipts"?merchant.trim():undefined,
          serviceKey:tab==="subscriptions"?service:undefined,mimeType:file.type,size:file.size,observedAt:purchaseDate})
      });
      const intentData=await intent.json();
      if(!intent.ok)throw new Error(intentData.error||"Could not start upload");
      const supabase=createClient(url,anonKey,{auth:{persistSession:false,autoRefreshToken:false}});
      const {error:uploadError}=await supabase.storage.from("handle-evidence")
        .uploadToSignedUrl(intentData.path,intentData.token,file,{contentType:file.type});
      if(uploadError)throw new Error("Private upload failed: "+uploadError.message);
      const final=await fetch(API+"/evidence/finalize",{
        method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({id:intentData.id})
      });
      const result=await final.json();
      if(!final.ok)throw new Error(result.error||"Upload could not be finalized");
      setMessage("Evidence received. It is pending review—not yet a verified purchase or subscription.");
      setMerchant("");setFile(null);
      const input=document.getElementById("handle-receipt-image") as HTMLInputElement|null;
      if(input)input.value="";
      await load();
    }catch(e){setError(e instanceof Error?e.message:"Upload failed");}
    finally{setSubmitting(false);}
  };
  const remove=async(id:string)=>{
    if(!window.confirm("Permanently remove this evidence and its private file?"))return;
    setError("");setMessage("");
    try{
      const res=await fetch(API+"/evidence",{
        method:"DELETE",credentials:"include",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({id})
      });
      const result=await res.json();
      if(!res.ok)throw new Error(result.error||"Unable to remove evidence");
      setMessage("Evidence removed.");
      await load();
    }catch(e){setError(e instanceof Error?e.message:"Could not delete evidence");}
  };
  const copy=async()=>{
    if(!data?.forwardingAddress)return;
    try{await navigator.clipboard.writeText(data.forwardingAddress);setCopied(true);}
    catch{setError("Copy unavailable. Please select and copy the address manually.");}
  };
  const evidence=useMemo(()=>data?.records.filter(r=>tab==="receipts"?r.kind==="receipt":r.kind==="subscription")||[],[data,tab]);
  const pending= data?.records.filter(r=>r.status==="pending").length||0;

  return <main className="h-page h-proof"><div className="h-container">
    <header className="h-proof-hero">
      <div><span className="h-eyebrow"><FiShield size={15}/> VERIFIED SIGNALS / ON YOUR TERMS</span>
        <h1 className="h-display">Turn purchases into<br/><span className="h-gradient-text">permissioned proof.</span></h1>
        <p className="h-lead">A receipt says you bought it. A recurring confirmation adds confidence you still use it. Handle lets you manage those claims privately—and choose if they ever become shareable.</p>
        <div className="h-hero-actions"><span className="h-pill h-pill-info"><FiLock/> Raw evidence stays private</span><span className="h-pill h-pill-warning"><FiClock/> Confidence changes with time</span></div>
      </div>
      <div className="h-proof-hero-graphic" aria-hidden="true"><div className="h-proof-orbit"><FiCreditCard size={39}/><span className="h-proof-check"><FiCheckCircle/></span></div><span>PROVENANCE → CONSENT → VALUE</span></div>
    </header>
    <section className="h-section">
      <div className="h-proof-tabs" role="tablist" aria-label="Evidence type">
        <button role="tab" type="button" aria-selected={tab==="receipts"} className={tab==="receipts"?"active":""} onClick={()=>setTab("receipts")}><FiFileText/> Purchase receipts</button>
        <button role="tab" type="button" aria-selected={tab==="subscriptions"} className={tab==="subscriptions"?"active":""} onClick={()=>setTab("subscriptions")}><FiTv/> Streaming subscriptions</button>
      </div>
      {error&&<p role="alert" className="h-proof-alert">{error}</p>}
      {message&&<p role="status" className="h-proof-success">{message}</p>}
      {!isAuthenticated?(
        <div className="h-proof-signin h-panel"><FiLock size={29}/><h2>Connect your Handle identity first.</h2><p>Receipt records and subscription evidence are private to your account. Sign in with Google before sending or uploading anything.</p><button type="button" className="h-btn h-btn-primary" disabled={isLoading} onClick={loginWithGoogle}>Sign in with Google <FiArrowRight/></button></div>
      ):(
        <div className="h-proof-grid">
          <div className="h-proof-left">
            <section className="h-panel h-panel-padding" aria-label="Forward an email">
              <span className="h-eyebrow"><FiMail size={15}/> EMAIL EVIDENCE</span>
              <h2>Forward a confirmation.</h2>
              <p>From <strong>{user?.email||"your verified Google address"}</strong>, forward a store receipt or streaming confirmation. We match the sender to your account and put the evidence into review.</p>
              {data?.forwardingAddress?(
                <div className="h-proof-email-box"><code>{data.forwardingAddress}</code><button type="button" onClick={copy} aria-label="Copy forwarding email"><FiCopy size={16}/>{copied?"Copied":"Copy"}</button></div>
              ):<div className="h-proof-setup"><FiClock size={17}/> Email forwarding isn't configured yet. An inbox domain and webhook secret are required.</div>}
              <small>Receiving an email does not prove a subscription is active. Every claim stays pending until independently reviewed.</small>
            </section>
            <section className="h-panel h-panel-padding" aria-label="Upload receipt image">
              <span className="h-eyebrow"><FiUploadCloud size={15}/> PRIVATE IMAGE EVIDENCE</span>
              <h2>{tab==="receipts"?"Upload a receipt.":"Upload subscription proof."}</h2>
              <p>Images are sent directly to a private Supabase Storage bucket with a short-lived signed upload token.</p>
              <form className="h-proof-form" onSubmit={upload}>
                {tab==="receipts"?<label>Merchant or store name<input className="h-input" required maxLength={100} placeholder="e.g., Local bookstore" value={merchant} onChange={e=>setMerchant(e.target.value)}/></label>:
                <label>Streaming service<select className="h-select" value={service} onChange={e=>{setService(e.target.value);setSearchParams({tab:"subscriptions",service:e.target.value});}}>{streaming.map(s=><option key={s.key} value={s.key}>{s.name}</option>)}</select></label>}
                <label>Purchase / confirmation date<input className="h-input" type="date" value={purchaseDate} onChange={e=>setPurchaseDate(e.target.value)} max={today} required/></label>
                <label>Receipt image (JPG, PNG, WebP, max 6 MB)<input id="handle-receipt-image" type="file" accept="image/jpeg,image/png,image/webp" onChange={e=>setFile(e.target.files?.[0]||null)} required/></label>
                <button type="submit" className="h-btn h-btn-primary" disabled={submitting||loading||!data?.uploadsEnabled||!file}>{submitting?"Uploading securely…":"Submit for review"} <FiArrowRight size={16}/></button>
                {!data?.uploadsEnabled&&<small>Private upload isn't enabled yet. Configure Supabase Storage and the backend credentials.</small>}
              </form>
              <p className="h-proof-note">No OCR or automatic verification is performed. The declared date and merchant are claims until reviewed.</p>
            </section>
          </div>
          <div className="h-proof-right">
            {tab==="subscriptions"&&<section className="h-panel h-panel-padding">
              <div className="h-section-heading"><div><span className="h-small-label">YOUR STREAMING PROFILE</span><h2>Membership signals</h2></div><span className="h-pill h-pill-info">{streaming.length} services</span></div>
              <div className="h-stream-services">
                {streaming.map(c=>{
                  const signal=data?.subscriptions.find(s=>s.serviceKey===c.key);
                  const waiting=data?.records.filter(r=>r.serviceKey===c.key&&r.status==="pending").length||0;
                  return <div className="h-stream-service" key={c.key}>
                    <ConnectorLogo connector={c}/><div><strong>{c.name}</strong><small>{signal?(signal.status==="current"?"Fresh evidence":signal.status==="expired"?"Proof expired":"Evidence aging"):"No verified subscription"}{waiting>0?" · "+waiting+" pending":""}</small></div>
                    <span className={"h-stream-score "+(signal?.status==="current"?"current":"")}>{signal?signal.confidence+"%":"—"}</span>
                  </div>;
                })}
              </div>
              <p className="h-proof-note">A score is only calculated from reviewed evidence. It is neither a billable amount nor confirmation of a currently paid subscription.</p>
            </section>}
            <section className="h-panel h-panel-padding">
              <div className="h-section-heading"><div><span className="h-small-label">YOUR EVIDENCE VAULT</span><h2>{tab==="receipts"?"Purchase history":"Confirmation history"}</h2></div><button type="button" className="h-proof-refresh" onClick={()=>void load()} disabled={loading} aria-label="Refresh evidence"><FiRefreshCw/></button></div>
              <p className="h-proof-caption">{pending} record{pending===1?"":"s"} awaiting review across all evidence.</p>
              {loading&&<p className="h-proof-caption">Loading private evidence…</p>}
              {evidence.length===0&&!loading?<div className="h-proof-empty"><FiFileText size={27}/><strong>Nothing submitted yet.</strong><p>Forward an email or upload a receipt to begin. No claims are treated as verified automatically.</p></div>:
              <div className="h-proof-records">{evidence.map(record=><div className="h-proof-record" key={record.id}>
                <div><span className="h-pill">{record.method==="email"?"Email":"Private image"}</span><strong>{record.serviceKey?(streaming.find(s=>s.key===record.serviceKey)?.name||record.serviceKey):(record.merchant||"Receipt")}</strong>
                  <small>{new Date(record.createdAt).toLocaleDateString()} · {record.observedAt?"Claimed date "+new Date(record.observedAt).toLocaleDateString():"No claimed date"}</small></div>
                <div className="h-proof-record-actions"><span className={"h-proof-status "+record.status}>{labels[record.status]||record.status}</span><button aria-label="Delete evidence" title="Delete evidence" type="button" onClick={()=>void remove(record.id)}><FiTrash2 size={15}/></button></div>
              </div>)}</div>}
            </section>
          </div>
        </div>
      )}
    </section>
    <section className="h-section h-proof-explainer h-panel h-panel-padding">
      <div className="h-section-heading"><div><span className="h-small-label">TIME-DECAYING EVIDENCE MODEL</span><h2>A one-time receipt is a snapshot. Renewals are a signal.</h2><p>Interactive illustration of the proposed model—not based on your personal data.</p></div><FiTrendingDown size={27}/></div>
      <div className="h-proof-simulator"><div><span className="h-small-label">DAYS SINCE LAST VERIFIED CONFIRMATION</span><strong>{days} days</strong><input type="range" min={0} max={120} step={5} value={days} onChange={e=>setDays(Number(e.target.value))} aria-label="Days since last verified subscription confirmation"/><div className="h-proof-range-markers"><span>Today</span><span>60 days</span><span>120 days</span></div></div>
      <div className="h-proof-simulator-score"><span className="h-small-label">ILLUSTRATIVE CONFIDENCE</span><strong>{demoFreshness(days)}%</strong><small>30-day half-life · expires after 90 days · illustrative</small></div></div>
      <div className="h-proof-explain-steps"><div><FiCheckCircle/><strong>Review first</strong><span>No points from unverified uploads or forwarded emails.</span></div><div><FiRefreshCw/><strong>Renew monthly</strong><span>Distinct verified renewal periods boost baseline confidence, capped at 95%.</span></div><div><FiLock/><strong>Always revocable</strong><span>Evidence is private; marketplace sharing requires a separate permission.</span></div></div>
    </section>
  </div></main>;
};
export default ReceiptEvidencePage;
